import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import type { FunctionComponent, MouseEventHandler } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import type { GlobalSearchJobSkill } from '../../../../data-contract/global-search';
import {
    AdditionalItemsCountButton,
    ExpandOrCollapseDataButton,
} from '../../../../design/components/button/ExpandDataButton';
import { SkillChipWithDrawer } from '../../skills/SkillChipWithDrawer';

type SkillCellProps = {
    readonly defaultVisibleSkillsCount: number;
    readonly prioritySkillIds?: readonly string[];
    readonly skills: readonly GlobalSearchJobSkill[];
};

export const SkillCell: FunctionComponent<SkillCellProps> = ({
    defaultVisibleSkillsCount,
    prioritySkillIds,
    skills,
}) => {
    let allSkills = [...skills];
    let visibleSkillsCount = defaultVisibleSkillsCount;
    const { translate } = useContext(ApplicationContext);
    const [showAllSkills, setShowAllSkills] = useState(false);

    if (is.array(prioritySkillIds) && is.nonEmptyArray(prioritySkillIds)) {
        allSkills = allSkills.sort((s1, s2) => {
            const index1 = prioritySkillIds.indexOf(s1.id);
            const index2 = prioritySkillIds.indexOf(s2.id);

            if (index1 >= 0 && index2 >= 0) {
                return index1 - index2;
            }
            if (index1 >= 0) {
                return -1;
            }
            if (index2 >= 0) {
                return 1;
            }

            return 0;
        });

        visibleSkillsCount = allSkills.filter((s) => prioritySkillIds.includes(s.id)).length;

        if (visibleSkillsCount < defaultVisibleSkillsCount) {
            visibleSkillsCount = defaultVisibleSkillsCount;
        }
    }

    const skillList = allSkills.slice(0, visibleSkillsCount);
    const [visibleSkills, setVisibleSkills] = useState<readonly GlobalSearchJobSkill[]>(skillList);

    const onAdditionalSkillCounterClick: MouseEventHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setShowAllSkills(true);
        setVisibleSkills(allSkills);
    };

    const onCollapseSkillCounterClick: MouseEventHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setVisibleSkills(skillList);
        setShowAllSkills(false);
    };

    const additionalSkillsLength = allSkills.length - visibleSkills.length;

    return (
        <Grid
            container={true}
            sx={{
                gap: 1,
            }}
        >
            {visibleSkills.map((skill) => (
                <SkillChipWithDrawer
                    key={skill.id}
                    isSynced={skill.isSynced}
                    label={skill.title}
                    skillId={skill.id}
                    type={skill.skillType}
                />
            ))}
            {additionalSkillsLength > 0 && (
                <AdditionalItemsCountButton number={additionalSkillsLength} onClick={onAdditionalSkillCounterClick} />
            )}
            {showAllSkills && (
                <ExpandOrCollapseDataButton
                    label={`- ${translate('common.show-less')}`}
                    onClick={onCollapseSkillCounterClick}
                />
            )}
        </Grid>
    );
};
